// src/pages/ComprarTokens.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { motion } from "framer-motion";
import { FaCoins } from "react-icons/fa";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../context/authContext";
import toast from "react-hot-toast";
import { volverAClase } from "../utils/navigation";

export default function ComprarTokens() {
  const { user, comprarTokens, EstadoLogout } = useAuth();

  const [paquetes, setPaquetes] = useState([]);
  const [selectedPaquete, setSelectedPaquete] = useState(null);
  const navigate = useNavigate();
  const paypalRef = useRef();

  // 🔹 Frases motivacionales
  const frases = [
    "Lo que no hagas ahora, no lo harás nunca 🚀",
    "Invertir en ti siempre merece la pena 💪",
    "Cada pedalada cuenta, da el siguiente paso 🔥",
    "Hoy es el día perfecto para mejorar 🕒",
    "El futuro se construye pedaleando ahora 🏆",
  ];
  const frase = frases[Math.floor(Math.random() * frases.length)];

  // 🔹 Cargar paquetes desde Firestore
  useEffect(() => {
    const fetchPaquetes = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "paquetesTokens"));
        const data = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((p) => p.activo)
          .sort((a, b) => a.orden - b.orden);
        setPaquetes(data);
      } catch (error) {
        console.error("❌ Error cargando paquetes:", error);
        toast.error("Error al cargar los paquetes de tokens.");
      }
    };
    fetchPaquetes();
  }, []);

  // 🔹 Renderizar PayPal Button cuando hay paquete seleccionado
  useEffect(() => {
    if (selectedPaquete) {
      if (!document.querySelector("#paypal-sdk")) {
        const script = document.createElement("script");
        script.id = "paypal-sdk";
        script.src = `https://www.paypal.com/sdk/js?client-id=${process.env.REACT_APP_PAYPAL_CLIENT_ID
          }&currency=EUR`;
        script.async = true;
        script.onload = () => renderButton();
        document.body.appendChild(script);
      } else {
        renderButton();
      }
    }

    function renderButton() {
      if (window.paypal && paypalRef.current) {
        paypalRef.current.innerHTML = ""; // limpiar antes de renderizar
        window.paypal
          .Buttons({
            createOrder: (data, actions) => {
              return actions.order.create({
                purchase_units: [
                  {
                    description: `${selectedPaquete.tokens} tokens`,
                    amount: {
                      currency_code: "EUR",
                      value: selectedPaquete.precio.replace(" €", ""),
                    },
                  },
                ],
              });
            },
            onApprove: async (data, actions) => {
              try {
                const order = await actions.order.capture();
                await comprarTokens(user.uid, selectedPaquete.tokens, {
                  precio: selectedPaquete.precio,
                  transaccionId: order.id,
                  metodo: "PayPal",
                });
                await EstadoLogout(); // 🔹 refrescar estado
                toast.success("✅ Compra realizada con éxito.");
                navigate("/comprobacion");
              } catch (err) {
                console.error("❌ Error procesando la compra:", err);
                toast.error("Error al procesar la compra.");
              }
            },
            onError: (err) => {
              console.error("❌ Error en PayPal:", err);
              toast.error("Hubo un problema con PayPal. Intenta de nuevo.");
            },
          })
          .render(paypalRef.current);
      }
    }
  }, [selectedPaquete, user, navigate, comprarTokens, EstadoLogout]);

  return (
    <div className="w-screen min-h-screen flex flex-col items-center bg-gradient-to-br from-gray-900 via-black to-gray-800 p-6">
      {/* Título */}
      <h2 className="text-3xl font-extrabold mb-2 text-white drop-shadow-lg">
        🪙 Adquirir Tokens
      </h2>
      <p className="text-gray-400 mb-10 text-center">
        Selecciona la cantidad de tokens que deseas adquirir
      </p>

      {/* Paquetes */}
      <div className="grid gap-6 w-full max-w-2xl">
        {paquetes.map((p) => (
          <motion.div
            key={p.id}
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
            onClick={() => setSelectedPaquete(p)}
            className={`p-6 rounded-2xl cursor-pointer shadow-lg transition-all backdrop-blur-lg 
              ${selectedPaquete?.id === p.id
                ? "bg-yellow-400/20 border-2 border-yellow-400"
                : "bg-white/10 border border-white/10 hover:bg-white/20"
              }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <FaCoins className="text-yellow-400" /> {p.tokens} tokens
                </h3>
                <p className="text-gray-300">{p.precio}</p>
                {p.descuento && (
                  <span className="inline-block mt-2 px-3 py-1 text-sm bg-green-600 text-white rounded-full font-semibold">
                    🔥 Con descuento
                  </span>
                )}
              </div>
              {selectedPaquete?.id === p.id && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="text-yellow-400 font-bold text-lg"
                >
                  ✓
                </motion.span>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* PayPal Button container */}
      {selectedPaquete && (
        <div className="mt-10 w-full max-w-md">
          <div
            ref={paypalRef}
            className="paypal-buttons-container flex justify-center"
          />
        </div>
      )}

      {/* Botón volver */}

      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => volverAClase(navigate, user?.uid)}
        className="mt-12 px-10 py-4 bg-gradient-to-r from-gray-700 to-gray-800 text-white font-bold rounded-2xl shadow-md hover:opacity-90 transition"
      >
        ⬅️ Volver
      </motion.button>

      {/* Frase motivacional */}
      <p className="mt-6 text-gray-400 italic text-center">{frase}</p>
    </div>
  );
}
